import { useEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from './cn';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Optional header title. */
  title?: React.ReactNode;
  /** Optional sub-title / description under the title. */
  description?: React.ReactNode;
  /** Optional leading icon rendered in a branded badge next to the title. */
  icon?: React.ReactNode;
  /** Sticky footer content (usually action buttons). */
  footer?: React.ReactNode;
  size?: DialogSize;
  /** Hide the top-right close button. */
  hideCloseButton?: boolean;
  /** Close when clicking the backdrop. Default true. */
  closeOnBackdrop?: boolean;
  /** Class for the panel. */
  className?: string;
  children?: React.ReactNode;
  'aria-label'?: string;
}

const sizes: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Accessible modal dialog — replaces MUI <Dialog />.
 * Spring entry via motion, focus trap, Esc + backdrop close, body scroll lock,
 * focus restored on close. Portaled to document.body.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  icon,
  footer,
  size = 'md',
  hideCloseButton,
  closeOnBackdrop = true,
  className,
  children,
  'aria-label': ariaLabel,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Body scroll lock + focus management while open
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panelRef.current)?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = overflow;
      window.clearTimeout(focusTimer);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[1300] flex items-end justify-center p-3 sm:items-center sm:p-6">
          <motion.div
            className="absolute inset-0 bg-overlay/60 backdrop-blur-md dark:bg-overlay/75"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeOnBackdrop ? onClose : undefined}
          />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            // Prefer the visible title as the accessible name; fall back to aria-label.
            aria-labelledby={title ? titleId : undefined}
            aria-label={title ? undefined : ariaLabel}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className={cn(
              'relative z-[1] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-2xl outline-none',
              'border border-primary/15 dark:border-primary/25',
              'bg-surface dark:bg-surface-elevated/95 backdrop-blur-2xl',
              'shadow-pop',
              sizes[size],
              className,
            )}
          >
            {(title || icon || !hideCloseButton) && (
              <div className="flex items-start gap-4 px-6 pt-6 pb-4">
                {icon && (
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/15">
                    {icon}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  {title && (
                    <h2 id={titleId} className="text-lg font-bold leading-tight text-content">{title}</h2>
                  )}
                  {description && (
                    <p className="mt-1 text-sm leading-snug text-muted">{description}</p>
                  )}
                </div>
                {!hideCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="-mr-1.5 -mt-1.5 shrink-0 rounded-lg p-1.5 text-muted transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-1 pt-0.5">{children}</div>

            {footer && (
              <div className="flex items-center justify-end gap-3 border-t border-line/80 bg-surface/60 px-6 py-4 dark:bg-surface-elevated/40">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
